/* Simple synthesized notification chime using WebAudio.
 *
 * Two ascending square-wave tones with a quick decay envelope. Sounds like a
 * classic "new-order" ding. Uses a single lazy AudioContext so we don't run
 * afoul of the browser's autoplay policy — the AudioContext is created on
 * first `playChime()` invocation (which is always triggered by a user gesture
 * or a SW message following an already-granted permission). */

let ctx = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn("notificationSound: AudioContext unavailable:", e);
      ctx = null;
    }
  }
  // Resume if it was suspended by an autoplay policy.
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

function beep(freq, startAt, duration, gain = 0.18) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + startAt;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}

export function playChime() {
  // Two ascending tones — 880Hz then 1320Hz.
  beep(880, 0, 0.18);
  beep(1320, 0.16, 0.22);
}

/* Warm-up the AudioContext during a real user gesture (e.g. when the admin
 * clicks "Activate notifications"). After this, `playChime()` from a SW-posted
 * message will work even when the tab is backgrounded. */
export function primeAudio() {
  ensureCtx();
}

/* PASO 3 (F3B) — sonidos consolidados desde App.js (código verbatim). */
export function playChimeSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    const notes = [880, 1108.73, 1318.51]; // A5, C#6, E6 - major chord
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.28, now + i * 0.12 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.7);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.75);
    });
    setTimeout(() => { try { ctx.close(); } catch (err) { console.debug("chime ctx.close failed:", err); } }, 1500);
  } catch (e) {
    console.warn("Chime playback failed", e);
  }
}

export function playChatChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const start = (audioCtx) => {
      const now = audioCtx.currentTime;
      const notes = [1046.5, 1568.0]; // C6 → G6
      notes.forEach((freq, i) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = "triangle";
        osc.frequency.value = freq;
        const t0 = now + i * 0.09;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.35);
      });
      setTimeout(() => { try { audioCtx.close(); } catch (err) { console.debug("chat chime ctx.close failed:", err); } }, 900);
    };
    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().then(() => start(ctx)).catch(() => start(ctx));
    } else {
      start(ctx);
    }
  } catch (e) {
    console.warn("Chat chime failed", e);
  }
}
